import axios from 'axios';
import getAuthSession from './auth';
import moment from 'moment';
import { AssessmentSurveyAssessment } from '../interfaces/assessmentSurveyAssessments.interface';
import type { GridSortModel, GridFilterModel } from '@mui/x-data-grid-pro';
import { toIsoDateTime } from '../utils/adminDashboardUtils';

export type GetAssessmentSurveyAssessmentsArgs = {
  page: number;
  pageSize: number;
  statuses?: string[];
  ordering?: string;
  filters?: Record<string, any>;
};

export type GetAssessmentSurveyAssessmentsForDataGridArgs = {
  page: number;
  pageSize: number;
  statuses?: string[];
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
};

export type AssessmentSurveyAssessmentsDataGridResponse = {
  results: AssessmentSurveyAssessment[];
  count: number;
};

function mapGridToApiField(field: string) {
  const FIELD_MAP: Record<string, string> = {
    vendorName: 'company_name',
    siteOrApplicationName: 'site_app_name',
    assessorCompanyName: 'assessor_company_name',
    assessorName: 'assessor_name',
    assessorLevel: 'assessor_level',
    dateAccepted: 'date_accepted',
    submittedDate: 'submitted_date',
  };

  return FIELD_MAP[field] ?? field;
}

export async function getAssessmentSurveyAssessmentsForDataGrid({
  page,
  pageSize,
  statuses = ['Submitted'],
  sortModel,
  filterModel,
}: GetAssessmentSurveyAssessmentsForDataGridArgs): Promise<AssessmentSurveyAssessmentsDataGridResponse> {
  const authSession = await getAuthSession();

  const params: Record<string, any> = {
    limit: pageSize,
    offset: page * pageSize,
  };

  if (statuses?.length) {
    params.status = statuses;
  }

  const sort = sortModel?.[0];
  if (sort?.field && sort?.sort) {
    const apiField = mapGridToApiField(sort.field);
    params.ordering = sort.sort === 'asc' ? apiField : `-${apiField}`;
  }

  if (filterModel?.items?.length) {
    filterModel.items.forEach((item) => {
      if (item.value === undefined || item.value === null || String(item.value).trim() === '') return;
      if (!item.columnField) return;

      const apiField = mapGridToApiField(item.columnField);

      let value = item.value;

      if (apiField === 'date_accepted' || apiField === 'submitted_date') {
        const iso = toIsoDateTime(value);
        if (!iso) return;
        value = moment(iso).format('YYYY-MM-DD');
      }

      params[apiField] = value;
    });
  }

  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/assessment-survey-assessments/`,
    {
      headers: {
        Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
      params,
      paramsSerializer: (p) => {
        const sp = new URLSearchParams();
        Object.entries(p).forEach(([k, v]) => {
          if (Array.isArray(v)) v.forEach((x) => sp.append(k, String(x)));
          else sp.append(k, String(v));
        });
        return sp.toString();
      },
    },
  );

  return {
    results: response.data.results as AssessmentSurveyAssessment[],
    count: response.data.count as number,
  };
}
